import { ActionIcon, Badge, Checkbox, Group, Stack, Text, TextInput, Tooltip, UnstyledButton } from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useMemo, useRef, useState } from "react";
import type { RequiredSkill } from "../types/api";
import { fuzzyFilterStringList } from "../lib/catalogFuzzyFilter";
import { useSkillCatalog } from "../lib/useSkillCatalog";

type Props = {
  value: RequiredSkill[];
  onChange: (next: RequiredSkill[]) => void;
  label?: string;
  description?: string;
};

/**
 * Crash-proof skill editor. Avoids Mantine TagsInput — its optionsLockup blows up
 * when the suggestion list changes mid-typing (null.value).
 */
export function RequiredSkillsEditor({
  value,
  onChange,
  label = "",
  description = "",
}: Props) {
  const { flatOptions, labels } = useSkillCatalog();
  const [mustHave, setMustHave] = useState(false);
  const [draft, setDraft] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const allLabels = useMemo(() => flatOptions.map((o) => o.label), [flatOptions]);
  const codeByLabel = useMemo(() => {
    const map = new Map<string, string>();
    for (const o of flatOptions) {
      map.set(o.label.toLowerCase(), o.value);
      map.set(o.value.toLowerCase(), o.value);
    }
    return map;
  }, [flatOptions]);

  const suggestions = useMemo(() => {
    const picked = new Set(value.map((s) => s.skill.toLowerCase()));
    const ranked = draft.trim()
      ? fuzzyFilterStringList(allLabels, draft, 12)
      : allLabels.slice(0, 12);
    return ranked.filter((label) => !picked.has(label.toLowerCase()));
  }, [allLabels, draft, value]);

  function resolveTag(raw: string): RequiredSkill {
    const trimmed = raw.trim();
    const code = codeByLabel.get(trimmed.toLowerCase());
    const skillLabel = code && labels?.[code] ? labels[code] : trimmed;
    return {
      skill: skillLabel,
      skill_id: code ?? null,
      must_have: mustHave,
    };
  }

  function addRaw(rawInput: string) {
    const parts = rawInput.split(/[,;]/).map((p) => p.trim()).filter(Boolean);
    if (!parts.length) return;
    let next = value;
    for (const part of parts) {
      const skill = resolveTag(part);
      const key = skill.skill.toLowerCase();
      if (next.some((s) => s.skill.toLowerCase() === key || (s.skill_id && s.skill_id === skill.skill_id))) {
        continue;
      }
      next = [...next, skill];
    }
    if (next !== value) onChange(next);
    setDraft("");
    setOpen(false);
    inputRef.current?.focus();
  }

  function onDraftChange(next: string) {
    if (/[,;]/.test(next)) {
      const parts = next.split(/[,;]/);
      const toAdd = parts.slice(0, -1).join(",");
      const rest = parts[parts.length - 1] ?? "";
      if (toAdd.trim()) addRaw(toAdd);
      setDraft(rest);
      setOpen(true);
      return;
    }
    setDraft(next);
    setOpen(true);
  }

  return (
    <Stack gap="xs">
      <div>
        <Text size="sm" fw="var(--axio-font-weight-semibold)">
          {label}
        </Text>
        <Text size="xs" c="dimmed">
          {description}
        </Text>
      </div>

      <Group align="flex-end" wrap="nowrap" gap="md">
        <div style={{ flex: "var(--axio-number-one)", position: "relative" }}>
          <TextInput
            ref={inputRef}
            label="Add skill"
            placeholder="Search or type — comma / Enter to add"
            value={draft}
            onChange={(e) => onDraftChange(e.currentTarget.value)}
            onFocus={() => setOpen(true)}
            onBlur={() => {
              // Delay so suggestion click registers
              window.setTimeout(() => setOpen(false), 150);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (draft.trim()) addRaw(draft);
              }
              if (e.key === "Escape") setOpen(false);
            }}
          />
          {open && suggestions.length > 0 && (
            <Stack
              gap="var(--axio-unit-0)"
              style={{
                position: "absolute",
                zIndex: "var(--axio-number-twenty)",
                left: "var(--axio-unit-0)",
                right: "var(--axio-unit-0)",
                top: "var(--axio-percentage-full)",
                marginTop: "var(--axio-unit-4)",
                maxHeight: "var(--axio-unit-220)",
                overflowY: "auto",
                background: "var(--mantine-color-body)",
                border: "var(--axio-unit-1) solid var(--axio-color-border)",
                borderRadius: "var(--axio-radius)",
                boxShadow: "var(--axio-shadow-popover)",
              }}
            >
              {suggestions.map((s) => (
                <UnstyledButton
                  key={s}
                  px="sm"
                  py="var(--axio-unit-8)"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    addRaw(s);
                  }}
                  style={{ textAlign: "left", width: "var(--axio-percentage-full)" }}
                >
                  <Text size="sm">{s}</Text>
                </UnstyledButton>
              ))}
            </Stack>
          )}
        </div>
        <Checkbox
          label="Must-have"
          checked={mustHave}
          onChange={(e) => setMustHave(e.currentTarget.checked)}
          mb="var(--axio-unit-8)"
        />
        <ActionIcon
          variant="filled"
          color="violet"
          size="lg"
          mb="var(--axio-unit-4)"
          onClick={() => {
            if (draft.trim()) addRaw(draft);
          }}
          aria-label=""
        >
          <IconPlus size="var(--axio-unit-16)" />
        </ActionIcon>
      </Group>

      {value.length === 0 ? (
        <Text size="xs" c="dimmed">
          No skills yet — Explore ranking will be weaker until you add some.
        </Text>
      ) : (
        <Group gap="xs">
          {value.map((s, i) => (
            <Badge
              key={`${s.skill_id || s.skill}-${i}`}
              size="lg"
              variant={s.must_have ? "filled" : "light"}
              color={s.must_have ? "violet" : "gray"}
              rightSection={
                <Group gap="var(--axio-unit-4)">
                  <Tooltip label={s.must_have ? "Mark nice-to-have" : "Mark must-have"}>
                    <Text
                      size="xs"
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        onChange(value.map((sk, idx) => (idx === i ? { ...sk, must_have: !sk.must_have } : sk)))
                      }
                    >
                      {s.must_have ? "MUST" : "NICE"}
                      {!s.skill_id ? " · custom" : ""}
                    </Text>
                  </Tooltip>
                  <ActionIcon
                    size="xs"
                    variant="transparent"
                    c="inherit"
                    onClick={() => onChange(value.filter((_, idx) => idx !== i))}
                  >
                    <IconTrash size="var(--axio-unit-12)" />
                  </ActionIcon>
                </Group>
              }
            >
              {s.skill}
            </Badge>
          ))}
        </Group>
      )}
    </Stack>
  );
}
